import {
  DataMessageTypeEnum,
  TRACK_HISTORY_REQUEST_TOOL_NAME,
  type HITLActionRequest,
  type HITLDecision,
  type HITLRequestConfirmationDecision,
  type PromptOption,
  type ToolCallGenericMessage,
} from '@terra/shared-dto-chat-types';
import type { getToolConfig } from '../tool-config';

type ResolvedHITLRequestConfirmationDecision =
  NonNullable<HITLRequestConfirmationDecision>;

export const findOptionByValue = (
  options: ReadonlyArray<PromptOption>,
  value: string,
): PromptOption | undefined => options.find((opt) => opt.value === value);

export const readPendingReviewTitle = ({
  toolArgs,
  extractedData,
}: {
  toolArgs?: Record<string, unknown>;
  extractedData: unknown[];
}): string | undefined => {
  if (typeof toolArgs?.title === 'string' && toolArgs.title.trim()) {
    return toolArgs.title;
  }

  const firstPortalObject = extractedData[0];

  if (!firstPortalObject || typeof firstPortalObject !== 'object') {
    return undefined;
  }

  const portalObjectRecord = firstPortalObject as Record<string, unknown>;
  const title =
    portalObjectRecord.title ??
    portalObjectRecord.findingTitle ??
    portalObjectRecord.signalTitle ??
    portalObjectRecord.description ??
    portalObjectRecord.hypothesis;

  return typeof title === 'string' && title.trim() ? title : undefined;
};

export const readEditedActionArgs = (
  decision?: HITLDecision,
): Record<string, unknown> | undefined => {
  if (decision?.type !== 'edit') {
    return undefined;
  }

  const editedArgs = decision.editedAction.args;

  return editedArgs &&
    typeof editedArgs === 'object' &&
    !Array.isArray(editedArgs)
    ? (editedArgs as Record<string, unknown>)
    : undefined;
};

export const isPortalReviewContentType = (
  contentType?: string,
): contentType is 'finding' | 'signal' =>
  contentType === 'finding' || contentType === 'signal';

export const isSignalUpdateToolName = (toolName?: string): boolean =>
  toolName === 'update-signal-tool';

export const isStandaloneResolvedHITLCard = ({
  category,
  renderCard,
}: ReturnType<typeof getToolConfig>) =>
  category === 'executable' && !!renderCard;

export const shouldHideResolvedHitlWhenToolResultExists = ({
  contentType,
  toolName,
}: {
  contentType?: string;
  toolName?: string;
}) =>
  contentType === 'http' ||
  contentType === 'validated-http' ||
  toolName === TRACK_HISTORY_REQUEST_TOOL_NAME ||
  toolName === 'http-executor' ||
  toolName === 'coder-tool' ||
  toolName === 'python-tool';

export const catalogToolResultHasExecutableOutput = (
  toolResult: ToolCallGenericMessage,
): boolean => {
  if (
    toolResult.error !== undefined ||
    toolResult.type === DataMessageTypeEnum.ToolCallError
  ) {
    return true;
  }

  const output = toolResult.output;

  if (output === undefined) {
    return false;
  }

  if (typeof output !== 'object' || output === null) {
    return true;
  }

  const outputRecord = output as Record<string, unknown>;
  const toolName = toolResult.toolName ?? '';

  if (toolName === 'coder-tool' || toolName === 'python-tool') {
    const nestedOutput =
      outputRecord.output && typeof outputRecord.output === 'object'
        ? (outputRecord.output as Record<string, unknown>)
        : undefined;

    return Boolean(
      nestedOutput ||
        outputRecord.result !== undefined ||
        outputRecord.success !== undefined,
    );
  }

  return true;
};

export const isExecutedCatalogToolResult = (
  toolResult: ToolCallGenericMessage | undefined,
): boolean => {
  if (!toolResult || toolResult.status === 'aborted') {
    return false;
  }

  if (
    toolResult.status === 'error' ||
    toolResult.type === DataMessageTypeEnum.ToolCallError
  ) {
    return true;
  }

  return (
    (toolResult.status === 'completed' || toolResult.status === 'result') &&
    catalogToolResultHasExecutableOutput(toolResult)
  );
};

export const hasExecutedResultForActions = ({
  actionRequests,
  toolResultsById,
}: {
  actionRequests: ReadonlyArray<HITLActionRequest>;
  toolResultsById?: Record<string, ToolCallGenericMessage>;
}): boolean =>
  actionRequests.length > 0 &&
  actionRequests.every((actionRequest) => {
    const actionId = actionRequest.toolCallId ?? actionRequest.id;

    return Boolean(
      actionId && isExecutedCatalogToolResult(toolResultsById?.[actionId]),
    );
  });

export const getResolvedDecisionCardStatus = ({
  decision,
  hasToolResult,
  isLastMessage,
  isChatLoading,
}: {
  decision: ResolvedHITLRequestConfirmationDecision;
  hasToolResult?: boolean;
  isLastMessage?: boolean;
  isChatLoading?: boolean;
}) =>
  decision.type === 'reject' ||
  hasToolResult ||
  (!isLastMessage && !isChatLoading)
    ? 'completed'
    : 'started';

export const readHitlToolFailureMessage = (
  toolResult: ToolCallGenericMessage | undefined,
): string | undefined => {
  if (!toolResult) {
    return undefined;
  }

  const outputRecord =
    toolResult.output &&
    typeof toolResult.output === 'object' &&
    !Array.isArray(toolResult.output)
      ? (toolResult.output as Record<string, unknown>)
      : undefined;

  const isFailure =
    toolResult.status === 'error' ||
    toolResult.type === DataMessageTypeEnum.ToolCallError ||
    toolResult.error !== undefined ||
    outputRecord?.success === false;

  if (!isFailure) {
    return undefined;
  }

  const metadata =
    toolResult.metadata &&
    typeof toolResult.metadata === 'object' &&
    !Array.isArray(toolResult.metadata)
      ? (toolResult.metadata as Record<string, unknown>)
      : undefined;
  const errorLabel =
    typeof metadata?.errorLabel === 'string' ? metadata.errorLabel.trim() : '';

  if (errorLabel.length > 0) {
    return errorLabel;
  }

  const candidates = [
    typeof toolResult.error === 'string' ? toolResult.error.trim() : '',
    typeof outputRecord?.message === 'string' ? outputRecord.message.trim() : '',
    typeof toolResult.message === 'string' ? toolResult.message.trim() : '',
  ].filter((value) => value.length > 0);

  const safeCandidate = candidates.find(
    (value) =>
      !value.includes('Error invoking tool') &&
      !value.includes('kwargs') &&
      !value.includes('/node_modules/') &&
      !/^\s*at\s+\S+/m.test(value),
  );

  if (safeCandidate) {
    return safeCandidate;
  }

  return typeof outputRecord?.errorCode === 'string'
    ? outputRecord.errorCode
    : 'Request failed';
};
